/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Helper imports
import * as url from "helper/UrlHelper";
import {
  postData,
  getData,
  putData,
  uploadSingleFile,
  uploadMultipleFile,
} from "utils/Gateway";
import { getMemberOptions } from "helper/ContactHelper/ContactHelper";

import BreadCrumb from "components/Common/BreadCrumb/BreadCrumb";
import TabsHeader from "components/Common/TabsHeader/TabsHeader";
import AfterSaveEventModal from "../Modal/AfterSaveEventModal";
import { eventDetailsHeaderLinks } from "helper/EventHelper/EventHelper";
import { ecosystemSlug } from "Config/Config";
import { getAllCategories } from "helper/CategoryHelper/CategoryHelper";
import AccessDeniedView from "components/Common/AccessDeniedView/AccessDeniedView";
import EventForm from "../EventForm/EventForm";
import EventDetailsView from "../EventDetailsView/EventDetailsView";
import { getCurrentTime } from "helper/TimeCalculation";

/**
 * EventInfoBody Component
 *
 * This component manages the display and editing of event information.
 * It handles fetching event details, managing form states, uploading files,
 * and submitting event data to the backend. It also controls access based on user roles.
 */
const EventInfoBody = () => {
  // Hooks for internationalization, routing, and state management
  const { t } = useTranslation();
  const params = useParams();
  const history = useHistory();

  // User and module access information from local storage
  const token = localStorage.getItem("token");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));
  const moduleAccess = localStorage.getItem("moduleaccess") || "";

  // Current date value for default date fields
  const todayValue = new Date().toISOString().split("T")[0];

  // Extract 'type' parameter from URL query string
  const queryParams = new URLSearchParams(window.location.search);
  const typeParam = queryParams.get("type");

  /**
   * State to manage loading status for data fetches.
   * @type {boolean}
   */
  const [isLoading, setIsLoading] = useState(false);

  /**
   * State to hold all event-related data, including form fields,
   * upload statuses, and validation errors.
   * @type {object}
   */
  const [eventData, setEventField] = useState({
    eventType: null,
    company: null,
    startDate: todayValue,
    endDate: todayValue,
    startTime: getCurrentTime(),
    endTime: getCurrentTime(),
    isRoot: false,
    parentEvent: null,
    title: "",
    summary: "",
    description: "",
    isPhysical: true,
    location: "",
    city: "",
    country: "",
    zipcode: "",
    mapUrl: "",
    isVirtual: false,
    virtualUrl: "",
    contactPhone: "",
    contactEmail: "",
    website: "",
    category: null,
    searching: null,
    interest: null,
    image: "",
    imageId: null,
    documents: [],
    documentIds: [],
    isPrivate: false,
    autoAccept: false,
    buyFee: "",
    sellFee: "",
    isUploadingImage: false,
    isUploadingDocs: false,
    isSaving: false,
    error: "",
    validation: { title: false, parentEvent: false },
    followers: [],
    followersValue: [],
  });

  /**
   * State to store the list of available event types.
   * @type {Array<object>}
   */
  const [eventTypeList, setEventTypeList] = useState([]);
  /**
   * State to store the list of available categories.
   * @type {Array<object>}
   */
  const [categoryList, setCategoryList] = useState([]);
  /**
   * State to store the list of available 'searching' options.
   * @type {Array<object>}
   */
  const [searchingList, setsearchingList] = useState([]);
  /**
   * State to store the list of available 'interest' options.
   * @type {Array<object>}
   */
  const [interestList, setinterestList] = useState([]);
  /**
   * State to store options for parent events, used in dropdowns.
   * @type {Array<object>}
   */
  const [parentEventOptions, setParentEventOptions] = useState([]);
  /**
   * State to store the title used for filtering parent events.
   * @type {string}
   */
  const [parentEventTitle, setParentEventTitle] = useState("");

  /**
   * State to store the list of available followers.
   * @type {Array<object>}
   */
  const [followersInput, setFollowersInput] = useState("");
  const [followersOptions, setFollowersOptions] = useState([]);
  /**
   * State to store the list of available companies.
   * @type {Array<object>}
   */
  const [companyList, setCompanyList] = useState([]);
  /**
   * State to store the input value for company search.
   * @type {string}
   */
  const [companyInput, setCompanyInput] = useState("");
  /**
   * State to indicate if the current user is an event moderator.
   * @type {boolean}
   */
  const [isEventModerator, setIsEventModerator] = useState(false);
  /**
   * State to store the member status of the current user for the event.
   * @type {object}
   */
  const [memberStatus, setMemberStatus] = useState({});
  /**
   * State to store the ID of the newly saved event, used for redirection.
   * @type {string|null}
   */
  const [savedEventId, setSavedEventId] = useState(null);
  /**
   * State to indicate if a join event action is in progress.
   * @type {boolean}
   */
  const [isJoining, setIsJoining] = useState(false);
  /**
   * State to store common header objects for tabs.
   * @type {Array<object>}
   */
  const [commonHeaderObject, setCommonHeaderObject] = useState([]);

  /**
   * State to track if initial parameters (event types, categories, etc.) have been fetched.
   * @type {boolean}
   */
  const [isParamsFetched, setIsParamsFetched] = useState(false);

  /**
   * Breadcrumb navigation text configuration.
   * @type {Array<object>}
   */
  const breadcrumbText = [
    { title: t("Event"), link: "/admin/events/list" },
    { title: t("Event Info") },
  ];

  /**
   * Fetches all available event types from the API and updates the `eventTypeList` state.
   * Sets a default event type based on URL parameters or the first item in the list.
   * @async
   * @returns {Promise<void>}
   */
  const getEventTypes = async () => {
    try {
      let requestUrl = url.API_BASE_URL + url.API_GET_ALL_EVENT_TYPES;
      const response = await getData(requestUrl);

      console.log("response in event type------->", response);

      if (response.status) {
        setEventTypeList(response.data);
        // If URL has type param, set that as default, else use first
        if (typeParam) {
          const foundType = response.data.find(
            (item) => item.value === typeParam
          );
          if (foundType) {
            setEventField((prev) => ({ ...prev, eventType: foundType }));
          } else {
            setEventField((prev) => ({ ...prev, eventType: response.data[0] }));
          }
        } else {
          setEventField((prev) => ({ ...prev, eventType: response.data[0] }));
        }
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  /**
   * Fetches all event categories from the API and updates the `categoryList` state.
   * Uses 'GROUP' as the parent slug for category retrieval.
   * @async
   * @returns {Promise<void>}
   */
  const getAllCategoryList = async () => {
    try {
      const parentSlug = "EVENT";
      const response = await getAllCategories(ecosystemSlug, parentSlug);

      if (response) {
        setCategoryList(response);
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  /**
   * Fetches all 'searching' categories from the API and updates the `searchingList` state.
   * Uses 'searchings' as the parent slug for category retrieval.
   * @async
   * @returns {Promise<void>}
   */
  const getAllSearchings = async () => {
    try {
      const parentSlug = "searchings";
      const response = await getAllCategories(ecosystemSlug, parentSlug);

      if (response) {
        setsearchingList(response);
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  /**
   * Fetches all 'interest' categories from the API and updates the `interestList` state.
   * Uses 'interests' as the parent slug for category retrieval.
   * @async
   * @returns {Promise<void>}
   */
  const getAllInterests = async () => {
    try {
      const parentSlug = "interests";
      const response = await getAllCategories(ecosystemSlug, parentSlug);

      if (response) {
        setinterestList(response);
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  /**
   * Fetches a list of companies from the API based on the `companyInput` filter.
   * Updates the `companyList` state with the fetched data.
   * @async
   * @returns {Promise<void>}
   */
  const getCompanyList = async () => {
    try {
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_ALL_COMPANY_LIST +
        `?token=${token}&filtername=${companyInput}&modulefrom=backend`;

      console.log("url of company------>", requestUrl);

      const response = await getData(requestUrl);

      console.log("response in company------>", response);

      if (response.status) {
        setCompanyList(response.data);
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  /**
   * Fetches a list of parent events from the API, optionally filtered by `parentEventTitle`.
   * Updates the `parentEventOptions` state with the fetched data.
   * @async
   * @returns {Promise<void>}
   */
  const getAllParentEvents = async () => {
    try {
      let requestUrl =
        url.API_BASE_URL + url.API_GET_PARENT_EVENTS + `?token=${token}`;

      if (parentEventTitle !== "") {
        requestUrl += `&eventtitle=${parentEventTitle}`;
      }

      // console.log("url of event list---------->", requestUrl);

      const response = await getData(requestUrl);

      // console.log("response in parent event list------->", response);

      if (response.status) {
        setParentEventOptions(response.data);
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  /**
   * Fetches a list of followers from the API based on the `followersInput` filter.
   * Updates the `followersOptions` state with the fetched data.
   * @async
   * @returns {Promise<void>}
   */
  //function for get followers list
  const getFollowersList = async () => {
    try {
      const data = await getMemberOptions(followersInput);

      setFollowersOptions(data);
    } catch (error) {
      console.log(error.message);
    }
  };

  /**
   * Fetches location details (city, state, country) from Google Maps API
   * based on the `eventData.zipcode`. Updates the `eventData` state with the retrieved information.
   * @async
   * @returns {Promise<void>}
   */
  const handleLocationFromZip = async () => {
    if (!eventData.zipcode) return;

    try {
      const url = `https://maps.googleapis.com/maps/api/geocode/json?components=postal_code:${eventData.zipcode}&region=es&key=AIzaSyDt4dbtdHCHLj8grTzTVxpztvqUGDkvkaI`;
      const response = await getData(url);

      if (response.status === "OK" && response.results.length > 0) {
        const address = response.results[0];
        const components = address.address_components.reduce((acc, comp) => {
          comp.types.forEach((type) => {
            acc[type] = comp.long_name;
          });
          return acc;
        }, {});

        setEventField((prev) => ({
          ...prev,
          city: components.locality || prev.city,
          state: components.administrative_area_level_1 || "",
          country: components.country || prev.country,
        }));
      }
    } catch (error) {
      console.error("Error fetching location:", error);
    }
  };

  /**
   * Handles the upload of a single image file.
   * Sets `isUploadingImage` state, uploads the file, and updates `eventData`
   * with the image path and ID upon successful upload.
   * @async
   * @param {FileList} files - A FileList object containing the image file to upload.
   * @returns {Promise<void>}
   */
  const handleImageUpload = async (files) => {
    if (!files || !files[0]) return;

    setEventField((prev) => ({ ...prev, isUploadingImage: true }));
    try {
      const uploadUrl = `${url.API_BASE_URL}${url.API_SINGLE_FILE_UPLOAD}?token=${token}`;
      const response = await uploadSingleFile(uploadUrl, files[0]);

      if (response.status) {
        setEventField((prev) => ({
          ...prev,
          image: response.data.path,
          imageId: response.data._id,
          isUploadingImage: false,
        }));
      }
    } catch (error) {
      console.error("Image upload error:", error);
      setEventField((prev) => ({ ...prev, isUploadingImage: false }));
    }
  };

  /**
   * Handles the upload of multiple document files.
   * Sets `isUploadingDocs` state, uploads the files, and updates `eventData`
   * with the document paths and IDs upon successful upload.
   * @async
   * @param {FileList} files - A FileList object containing the document files to upload.
   * @returns {Promise<void>}
   */
  const handleDocumentUpload = async (files) => {
    if (!files || files.length === 0) return;

    setEventField((prev) => ({ ...prev, isUploadingDocs: true }));
    try {
      const uploadUrl = `${url.API_BASE_URL}${url.API_MULTIPLE_FILE_UPLOAD}?token=${token}`;
      const response = await uploadMultipleFile(uploadUrl, files);

      if (response.status) {
        const newFiles = response.data.map((file) => ({
          ...file,
          path: url.SERVER_URL + file.path,
        }));

        setEventField((prev) => ({
          ...prev,
          documents: [...prev.documents, ...newFiles],
          documentIds: [
            ...prev.documentIds,
            ...response.data.map((f) => f._id),
          ],
          isUploadingDocs: false,
        }));
      }
    } catch (error) {
      console.error("Document upload error:", error);
      setEventField((prev) => ({ ...prev, isUploadingDocs: false }));
    }
  };

  /**
   * Handles the action of a user joining an event.
   * Sets `isJoining` state, sends a join request to the API,
   * and re-fetches event details upon successful join.
   * @async
   * @returns {Promise<void>}
   */
  const joinEventHandler = async () => {
    try {
      setIsJoining(true);
      let apiData = {
        event: params.id,
        useremail: userInfo.email,
        secret: eventData.secret,
      };

      console.log("Join event Data------>", apiData);

      let requestURL =
        url.API_BASE_URL + url.API_JOIN_EVENT + `?token=${token}`;

      const response = await postData(requestURL, eventData);

      setIsJoining(false);
      console.log(response);
      if (response.status) {
        getEventDetails();
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  /**
   * Validates the event form fields.
   * Checks for required fields like `title` and `parentEvent` (if `isRoot` is true).
   * Updates `eventData.validation` state for invalid fields.
   * @returns {boolean} - True if the form is valid, false otherwise.
   */
  const validateForm = () => {
    let isValid = true;

    if (!eventData.title.trim()) {
      setEventField((prev) => ({
        ...prev,
        validation: { ...prev.validation, title: true },
      }));
      isValid = false;
    }

    if (eventData.isRoot === true && !eventData.parentEvent) {
      setEventField((prev) => ({
        ...prev,
        validation: { ...prev.validation, parentEvent: true },
      }));
      isValid = false;
    }

    return isValid;
  };

  /**
   * Handles the submission of the event form.
   * Validates the form, constructs the payload, and sends it to the API
   * for creating or updating an event. Shows a modal on successful save.
   * @async
   * @returns {Promise<void>}
   */
  const handleSubmit = async () => {
    if (!validateForm()) {
      setEventField((prev) => ({
        ...prev,
        error: t("Please fill required fields"),
      }));
      window.scrollTo(0, 0);
      return;
    }

    setEventField((prev) => ({ ...prev, isSaving: true }));

    const payload = {
      eventtype: eventData.eventType?.value,
      fromdate: eventData.startDate,
      todate: eventData.endDate,
      starttime: eventData.startTime,
      endtime: eventData.endTime,
      title: eventData.title,
      summary: eventData.summary,
      description: eventData.description,
      address: eventData.location,
      city: eventData.city,
      state: "",
      zipcode: eventData.zipcode,
      country: eventData.country,
      budget: "",
      website: eventData.website,
      isvirtual: eventData.isVirtual,
      email: eventData.contactEmail,
      phone: eventData.contactPhone,
      meeturl: eventData.virtualUrl,
      mapurl: eventData.mapUrl,
      logoid: eventData.imageId,
      uploads: eventData.documentIds,
      categories: eventData.category?.map((c) => c.value) || [],
      searchings: eventData.searching?.map((s) => s.value) || [],
      interests: eventData.interest?.map((i) => i.value) || [],
      followers: eventData.followers?.map((f) => f.value) || [],
      autoapproval: eventData.autoAccept,
      isprivate: eventData.isPrivate,
      isroot: eventData.isRoot ? false : true,
      parentid: eventData.parentEvent?.value || null,
      companyid: eventData.company?.value || null,
      buyparticipationfee: parseFloat(eventData.buyFee) || 0,
      sellparticipationfee: parseFloat(eventData.sellFee) || 0,
    };

    console.log("payload------->", payload);

    try {
      const baseUrl = url.API_BASE_URL;
      const endpoint = params.id
        ? `${baseUrl}${url.API_UPDATE_EVENT}/${params.id}?token=${token}`
        : `${baseUrl}${url.API_CREATE_EVENT}?token=${token}`;

      const method = params.id ? putData : postData;
      const response = await method(endpoint, payload);

      if (response.status) {
        setSavedEventId(response.data._id);
        const modal = new window.bootstrap.Modal(
          document.getElementById("saveEventinfo")
        );
        modal.show();
      }
    } catch (error) {
      console.error("Save error:", error);
    } finally {
      setEventField((prev) => ({ ...prev, isSaving: false }));
    }
  };

  /**
   * Handles the closing of the AfterSaveEventModal.
   * Redirects the user to the event list or the event info page based on `saveType`.
   * @param {string} [saveType="list"] - Determines where to redirect after closing the modal.
   *                                     "list" for event list, any other value for event info page.
   * @returns {void}
   */
  const afterSaveEventModalClose = (saveType = "list") => {
    if (saveType == "list") {
      history.push("/admin/events/list");
    } else {
      if (params.id) {
        history.push(`/admin/events/info/${params.id}`);
      } else {
        history.push(`/admin/events/info/${savedEventId}`);
      }
    }
  };

  /**
   * Fetches the details of a specific event using its ID from URL parameters.
   * Populates the `eventData` state with the fetched details.
   * Determines if the current user is an event moderator and fetches member status if not.
   * Updates header links based on event type and moderator status.
   * @async
   * @returns {Promise<void>}
   */
  const getEventDetails = async () => {
    try {
      setIsLoading(true);
      const eventDetailsUrl = `${url.API_BASE_URL}${url.API_GET_EVENT_DETAILS}/${params.id}?token=${token}&modulefrom=backend`;
      const eventResponse = await getData(eventDetailsUrl);

      console.log("event response------>", eventResponse);

      if (eventResponse.status) {
        const { data } = eventResponse;
        setEventField({
          eventType: data.selectedeventtype,
          company: data.selectedcompany,
          startDate: data.fromdate || todayValue,
          endDate: data.todate || todayValue,
          startTime: data.starttime || getCurrentTime(),
          endTime: data.endtime || getCurrentTime(),
          title: data.title || "",
          summary: data.summary || "",
          description: data.description || "",
          isRoot: data.isroot ? false : true,
          parentEvent: data.parentevent || null,
          isPhysical: data.isphysical || true,
          location: data.address || "",
          city: data.city || "",
          country: data.country || "",
          zipcode: data.zipcode || "",
          mapUrl: data.mapurl || "",
          isVirtual: data.isvirtual || false,
          virtualUrl: data.meeturl || "",
          contactPhone: data.phone || "",
          contactEmail: data.email || "",
          website: data.website || "",
          category: data.eventcategories || null,
          searching: data.eventsearchings || null,
          interest: data.eventinterests || null,
          followers: data.eventfollowers || [],
          image: data.logoid?.path || "",
          imageId: data.logoid?._id || null,
          documents:
            data.uploads?.map((file) => ({
              ...file,
              path: url.SERVER_URL + file.path,
            })) || [],
          documentIds: data.uploads?.map((file) => file._id) || [],
          isPrivate: data.isprivate || false,
          autoAccept: data.autoapproval || false,
          buyFee: data.buyparticipationfee || "",
          sellFee: data.sellparticipationfee || "",
          isUploadingImage: false,
          isUploadingDocs: false,
          isSaving: false,
        });

        const isModerator =
          ["ADMIN", "SUPER_ADMIN"].includes(userInfo.role?.slug) ||
          data.moderator._id.toString() === userInfo._id.toString();

        setIsEventModerator(isModerator);

        if (!isModerator) {
          const memberUrl = `${url.API_BASE_URL}${url.API_GET_EVENT_MEMBER_DETAILS_BY_QUERY}?token=${token}&eventid=${params.id}`;
          const memberResponse = await getData(memberUrl);
          console.log("member response------>", memberResponse);
          if (memberResponse.status) {
            if (memberResponse.data) {
              setMemberStatus({
                id: memberResponse.data._id,
                status: memberResponse.data.memberstatus,
                modStatus: memberResponse.data.moderatorstatus,
                inviteDate: memberResponse.data.requestdate,
                joinDate: memberResponse.data.joindate,
              });
            }
          }
        }

        setCommonHeaderObject(
          eventDetailsHeaderLinks(
            params.id,
            t,
            data?.eventtype?.slug,
            isModerator
          )
        );

        setIsLoading(false);
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  /**
   * useEffect hook to fetch event details when the `params.id` changes.
   * This ensures that event data is loaded when navigating to an event's info page.
   */
  useEffect(() => {
    if (params.id) {
      getEventDetails();
    }
  }, [params.id]);

  /**
   * useEffect hook to fetch all initial parameters (event types, categories, interests, searchings)
   * when the component mounts. Sets `isParamsFetched` to true upon completion.
   */
  useEffect(() => {
    const fetchAllParams = async () => {
      await Promise.all([
        getEventTypes(),
        getAllCategoryList(),
        getAllInterests(),
        getAllSearchings(),
      ]);
      setIsParamsFetched(true);
    };
    fetchAllParams();
  }, []);

  /**
   * useEffect hook to fetch parent events whenever `parentEventTitle` changes,
   * provided the title has a length greater than 0.
   */
  useEffect(() => {
    if (parentEventTitle.length > 0) {
      getAllParentEvents();
    }
  }, [parentEventTitle]);

  /**
   * useEffect hook to fetch company list whenever `companyInput` changes,
   * provided the input has a length greater than 1.
   */
  useEffect(() => {
    if (companyInput.length > 1) {
      getCompanyList();
    }
  }, [companyInput]);

  /**
   * useEffect hook to fetch followers options whenever `followersInput` changes,
   * provided the input has a length greater than 0.
   */
  useEffect(() => {
    if (followersInput.length > 0) {
      console.log("followersInput changed", followersInput);
      getFollowersList();
    }
  }, [followersInput]);

  /**
   * Conditional rendering: If the user does not have ADMIN/SUPER_ADMIN role
   * or does not have 'MOD_EVENT' module access, display an Access Denied view.
   */
  if (
    !["ADMIN", "SUPER_ADMIN"].includes(userInfo.role?.slug) &&
    !moduleAccess.includes("MOD_EVENT")
  ) {
    return <AccessDeniedView />;
  }

  /**
   * Conditional rendering: If initial parameters are still being fetched or
   * event details are loading, display a placeholder loading view.
   */
  if (!isParamsFetched || isLoading) {
    return (
      <div id="content_wrapper">
        {" "}
        <section className="event-details-wrapper bg-white pb-5">
          {" "}
          <div className="container-fluid px-lg-5 pt-4 pt-md-0">
            {" "}
            <div className="py-5">
              <div className="placeholder-glow">
                <span
                  className="placeholder col-12 mb-3"
                  style={{ height: 40, display: "block" }}
                ></span>
                <span
                  className="placeholder col-8 mb-3"
                  style={{ height: 30, display: "block" }}
                ></span>
                <span
                  className="placeholder col-6 mb-3"
                  style={{ height: 30, display: "block" }}
                ></span>
                <span
                  className="placeholder col-4 mb-3"
                  style={{ height: 30, display: "block" }}
                ></span>
                <span
                  className="placeholder col-12 mb-3"
                  style={{ height: 200, display: "block" }}
                ></span>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  /**
   * Main render block for the EventInfoBody component.
   * Displays TabsHeader if in edit mode, BreadCrumb, and either EventDetailsView
   * (if viewing an existing event and not a moderator) or EventForm (for creating/editing).
   * Includes AfterSaveEventModal for post-save actions.
   */
  return (
    <div id="content_wrapper">
      <section className="event-details-wrapper bg-white pb-5">
        {params.id && (
          <TabsHeader
            commonHeaderObject={commonHeaderObject}
            activeOption={t("Event Info")}
          />
        )}

        <div
          className={
            isParamsFetched ? "container-fluid px-lg-5 pt-4 pt-md-0" : "d-none"
          }
        >
          <BreadCrumb
            breadCrumbText={breadcrumbText}
            bottom={true}
            displayName={eventData?.title || ""}
          />

          {params.id && !isEventModerator ? (
            memberStatus.status === "1" && memberStatus.modStatus === "1" ? (
              <EventDetailsView
                eventData={eventData}
                memberStatus={memberStatus}
                t={t}
                joinEventHandler={joinEventHandler}
                isJoining={isJoining}
              />
            ) : (
              <AccessDeniedView />
            )
          ) : (
            <EventForm
              eventData={eventData}
              setEventField={(field) => (value) =>
                setEventField((prev) => ({ ...prev, [field]: value }))}
              eventTypeList={eventTypeList}
              categoryList={categoryList}
              searchingList={searchingList}
              interestList={interestList}
              companyList={companyList}
              followersOptions={followersOptions}
              parentEventOptions={parentEventOptions}
              setParentEventTitle={setParentEventTitle}
              setCompanyInput={setCompanyInput}
              setFollowersInput={setFollowersInput}
              handleLocationFromZip={handleLocationFromZip}
              handleImageUpload={handleImageUpload}
              handleDocumentUpload={handleDocumentUpload}
              handleSubmit={handleSubmit}
              t={t}
              isEditMode={!!params.id}
              isSaving={eventData.isSaving}
            />
          )}
        </div>
      </section>
      <AfterSaveEventModal
        afterSaveEventModalClose={afterSaveEventModalClose}
      />
    </div>
  );
};

export default EventInfoBody;
